/**
 * Central notification (toast) chokepoint.
 *
 * Everything that wants a toast imports `toast` from here instead of directly
 * from `sonner`, so there is ONE place to observe user-facing notifications.
 * The behaviour is identical to sonner — this is a faithful proxy — but it also
 * lets an observer (the upcoming session-replay SDK) record every toast as a
 * timeline event via `setToastListener`.
 *
 * The `<Toaster />` element itself still comes from sonner (see app/layout.tsx);
 * only the imperative `toast(...)` API is routed through here.
 */

import { toast as sonnerToast } from "sonner";

export type ToastKind =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "message"
  | "loading";

/** Observer signature. `message` is the string arg when the caller passed one. */
export type ToastListener = (kind: ToastKind, message: string) => void;

let listener: ToastListener | null = null;

/**
 * Register a single observer for user-facing toasts (the replay SDK). Passing
 * `null` clears it. Kept intentionally minimal — one listener, never throws.
 */
export function setToastListener(fn: ToastListener | null): void {
  listener = fn;
}

/** Methods whose calls we surface to the listener. */
const TRACKED = new Set<string>([
  "success",
  "error",
  "warning",
  "info",
  "message",
  "loading",
]);

function emit(kind: ToastKind, args: unknown[]): void {
  if (!listener) return;
  try {
    const message = typeof args[0] === "string" ? args[0] : "";
    listener(kind, message);
  } catch {
    /* an observer must never break a toast */
  }
}

/**
 * Drop-in replacement for sonner's `toast`. Callable (`toast("hi")`) and carries
 * every sonner method (`success`/`error`/`promise`/`dismiss`/`custom`/…). The
 * tracked methods additionally notify the observer; everything else passes
 * straight through unchanged.
 */
export const toast = new Proxy(sonnerToast, {
  get(target, prop, receiver) {
    const orig = Reflect.get(target, prop, receiver);
    if (typeof prop === "string" && TRACKED.has(prop) && typeof orig === "function") {
      return (...args: unknown[]) => {
        emit(prop as ToastKind, args);
        return (orig as (...a: unknown[]) => unknown).apply(target, args);
      };
    }
    return orig;
  },
  apply(target, _thisArg, args: unknown[]) {
    emit("message", args);
    return Reflect.apply(target as (...a: unknown[]) => unknown, target, args);
  },
}) as typeof sonnerToast;
